import { t } from "@lingui/macro"
import { memo } from "react"
import { Box, Typography } from "@mui/material"
import {
    LibraryBooksRounded,
    OutboxRounded,
    CachedRounded,
    NotInterested,
    PersonRounded,
    FeedbackRounded,
    HelpRounded,
    ThumbUpRounded,
    SettingsRounded,
    AdminPanelSettingsRounded,
    ContactSupportRounded,
    InfoRounded,
} from "@mui/icons-material"

import {
    MessageTypeKey,
    IMessageTypeProps,
    IMessageTypeSectionProps,
} from "src/components/ChatMessageJourney/types"

import { chatbot_MessageType } from "src/api"

const MESSAGE_TYPE_CONFIG: Record<MessageTypeKey, IMessageTypeProps> = {
    INTRODUCTION: {
        color: "rgba(156, 39, 176, 1)",
        label: "Introduction",
        icon: <InfoRounded sx={{ fontSize: "18px" }} />,
    },
    DISPATCH: {
        color: "rgba(30, 136, 229, 1)",
        label: "Dispatch",
        icon: <OutboxRounded sx={{ fontSize: "18px" }} />,
    },
    FEEDBACK_RESPONSE: {
        color: "rgba(76, 175, 80, 1)",
        label: "Feedback response",
        icon: <FeedbackRounded sx={{ fontSize: "18px" }} />,
    },
    CHAT_NO_ANSWER: {
        color: "rgba(189, 189, 189, 1)",
        label: "No response",
        icon: <NotInterested sx={{ fontSize: "18px" }} />,
    },
    USER_INPUT: {
        color: "rgba(255, 152, 0, 1)",
        label: "User input",
        icon: <PersonRounded sx={{ fontSize: "18px" }} />,
    },
    REASON_REQUEST: {
        color: "rgba(233, 30, 99, 1)",
        label: "Reason request",
        icon: <HelpRounded sx={{ fontSize: "18px" }} />,
    },
    CHAT_ANSWER: {
        color: "rgba(102, 187, 106, 1)",
        label: "Chat answer",
        icon: <LibraryBooksRounded sx={{ fontSize: "18px" }} />,
    },
    CHAT_ANSWER_OPTIONS: {
        color: "rgba(102, 187, 106, 1)",
        label: "Chat answer options",
        icon: <LibraryBooksRounded sx={{ fontSize: "18px" }} />,
    },
    CHAT_ANSWER_FILE: {
        color: "rgba(102, 187, 106, 1)",
        label: "Chat answer file",
        icon: <LibraryBooksRounded sx={{ fontSize: "18px" }} />,
    },
    QUESTION: {
        color: "rgba(245, 124, 0, 1)",
        label: "Clarification",
        icon: <CachedRounded sx={{ fontSize: "18px" }} />,
    },
    FEEDBACK_REQUEST: {
        color: "rgba(156, 39, 176, 1)",
        label: "Feedback request",
        icon: <ThumbUpRounded sx={{ fontSize: "18px" }} />,
    },
    SYSTEM: {
        color: "rgba(96, 125, 139, 1)",
        label: "System",
        icon: <SettingsRounded sx={{ fontSize: "18px" }} />,
    },
    ADMIN_INPUT: {
        color: "rgba(121, 85, 72, 1)",
        label: "Admin input",
        icon: <AdminPanelSettingsRounded sx={{ fontSize: "18px" }} />,
    },
    CONFIRM_CONTACT_DETAILS: {
        color: "rgba(0, 150, 136, 1)",
        label: "Confirm contact details",
        icon: <ContactSupportRounded sx={{ fontSize: "18px" }} />,
    },
}

const mapAPIToDisplayType = (
    type: chatbot_MessageType,
): MessageTypeKey | undefined => {
    switch (type) {
        case "INTRODUCTION":
        case "DISPATCH":
        case "FEEDBACK_RESPONSE":
        case "CHAT_NO_ANSWER":
        case "USER_INPUT":
        case "REASON_REQUEST":
        case "CHAT_ANSWER":
        case "CHAT_ANSWER_OPTIONS":
        case "CHAT_ANSWER_FILE":
        case "QUESTION":
        case "FEEDBACK_REQUEST":
        case "SYSTEM":
        case "ADMIN_INPUT":
        case "CONFIRM_CONTACT_DETAILS":
            return type
        default:
            return undefined
    }
}

const MessageType = memo(({ color, label, icon }: IMessageTypeProps) => (
    <Box
        sx={{
            bgcolor: color,
            color: "success.contrastText",
            padding: "5px",
            borderRadius: 1,
            display: "inline-flex",
            alignItems: "center",
        }}
    >
        {icon}
        <Typography sx={{ py: 0, px: 0.5, fontSize: "14px", fontWeight: 500 }}>
            {label}
        </Typography>
    </Box>
))

const MessageTypeSection = memo(({ messageType }: IMessageTypeSectionProps) => {
    const displayType = mapAPIToDisplayType(messageType)
    if (
        displayType === undefined ||
        MESSAGE_TYPE_CONFIG[displayType] === undefined
    ) {
        return null
    }

    return (
        <Box component="section" sx={{ marginBottom: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t`chat-message-journey.message-type`}
            </Typography>
            <MessageType {...MESSAGE_TYPE_CONFIG[displayType]} />
        </Box>
    )
})

export default MessageTypeSection
